import { useState } from 'react'
import { router } from '@inertiajs/react'
import { PlusCircleFilled, ScanOutlined } from '@ant-design/icons'
import EventLayout from '@/components/layouts/EventLayout'
import { Button, TableSearch, Select, message } from '@/components/ui'
import { PageHeader, ZeroState } from '@/components/patterns'
import ScanPointCard from '@/components/scan_points/ScanPointCard'
import ScanPointDrawer from '@/components/scan_points/ScanPointDrawer'
import DeleteConfirmModal from '@/components/scan_points/DeleteConfirmModal'

interface Event {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
}

interface ScanPoint {
  id: number
  name: string
  kind: string
  start_at: string | null
  end_at: string | null
  allow_anytime_scans: boolean
  allow_multiple_scans: boolean
  agenda_session_id: number | null
  agenda_session_name: string | null
  scan_count: number
}

interface AgendaSession {
  value: number
  label: string
  starts_at: string
  ends_at: string
}

interface IndexProps {
  event: Event
  scan_points: ScanPoint[]
  agenda_sessions: AgendaSession[]
  filter_options: string[]
}

export default function Index({ event, scan_points, agenda_sessions, filter_options }: IndexProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterKind, setFilterKind] = useState('all')
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editingScanPoint, setEditingScanPoint] = useState<ScanPoint | null>(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [scanPointToDelete, setScanPointToDelete] = useState<{ id: number; name: string } | null>(null)

  const handleSearch = () => {
    router.get(`/events/${event.slug}/scan_points`, {
      search: searchTerm || undefined,
      filter: filterKind !== 'all' ? filterKind : undefined
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleReset = () => {
    setSearchTerm('')
    router.get(`/events/${event.slug}/scan_points`, {
      filter: filterKind !== 'all' ? filterKind : undefined
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleFilterChange = (value: string) => {
    setFilterKind(value)
    router.get(`/events/${event.slug}/scan_points`, {
      search: searchTerm || undefined,
      filter: value !== 'all' ? value : undefined
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleCreateClick = () => {
    setEditingScanPoint(null)
    setDrawerVisible(true)
  }

  const handleEditClick = (scanPoint: ScanPoint) => {
    setEditingScanPoint(scanPoint)
    setDrawerVisible(true)
  }

  const handleDeleteClick = (id: number, name: string) => {
    setScanPointToDelete({ id, name })
    setDeleteModalVisible(true)
  }

  const handleDelete = () => {
    if (!scanPointToDelete) return

    router.delete(`/events/${event.slug}/scan_points/${scanPointToDelete.id}`, {
      onSuccess: () => {
        setDeleteModalVisible(false)
        setScanPointToDelete(null)
      },
      onError: () => {
        message.error('Failed to delete scan point')
      }
    })
  }

  const handleViewAttendees = (scanPointId: number) => {
    router.visit(`/events/${event.slug}/scan_points/${scanPointId}/attendees`)
  }

  const handleSubmit = (values: any) => {
    if (editingScanPoint) {
      // Update existing scan point
      router.patch(`/events/${event.slug}/scan_points/${editingScanPoint.id}`, {
        scan_point: values
      }, {
        onSuccess: () => {
          setDrawerVisible(false)
          setEditingScanPoint(null)
        },
        onError: () => {
          message.error('Failed to update scan point')
        }
      })
    } else {
      // Create new scan point
      router.post(`/events/${event.slug}/scan_points`, {
        scan_point: values
      }, {
        onSuccess: () => {
          setDrawerVisible(false)
        },
        onError: () => {
          message.error('Failed to create scan point')
        }
      })
    }
  }

  const showZeroState = scan_points.length === 0

  return (
    <EventLayout>
      <div className="">
        <PageHeader
          title="Scan Points"
          subtitle="Configure custom scan points or link to a session."
          actions={
            <Button
              variant="primary"
              icon={<PlusCircleFilled />}
              onClick={handleCreateClick}
            >
              Create Scan Point
            </Button>
          }
        />

        {/* Search and Filter */}
        <div className="px-6 py-4 flex items-center gap-3">
          <TableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
            onReset={handleReset}
            placeholder="Search Scan Points"
            showFilter={false}
          />
          <Select
            value={filterKind}
            onChange={handleFilterChange}
            style={{ width: 200 }}
            options={[
              { label: 'Scan Point: All', value: 'all' },
              ...filter_options.map(option => ({
                label: option.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                value: option
              }))
            ]}
          />
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {showZeroState ? (
            <ZeroState
              icon={<ScanOutlined className="text-6xl text-gray-300" />}
              title="No Scan Point Yet"
              description="No scan points yet. Create scan points for each check-in location, day or session."
              action={{
                label: 'Create Scan Point',
                icon: <PlusCircleFilled />,
                onClick: handleCreateClick
              }}
            />
          ) : (
            <div className="space-y-4">
              {scan_points.map((scanPoint) => (
                <ScanPointCard
                  key={scanPoint.id}
                  scanPoint={scanPoint}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onViewAttendees={handleViewAttendees}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drawers and Modals */}
      <ScanPointDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setEditingScanPoint(null)
        }}
        onSubmit={handleSubmit}
        editingScanPoint={editingScanPoint}
        agendaSessions={agenda_sessions}
        eventStartDate={event.start_date}
        eventEndDate={event.end_date}
      />

      <DeleteConfirmModal
        visible={deleteModalVisible}
        scanPointName={scanPointToDelete?.name || ''}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false)
          setScanPointToDelete(null)
        }}
      />
    </EventLayout>
  )
}
